import { useCallback, useEffect, useRef, useState } from 'react'
import { App as CapacitorApp } from '@capacitor/app'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  DEVICE_SETUP_STEPS,
  hasSeenTourVersion,
  isPermissionsResetPending,
  isSetupStepId,
  parseSetupRequests,
  pendingStepRequests,
  SETUP_DEEP_LINK_PARAM,
  SETUP_FLOW_VERSION,
  summarizeSetup,
  type SetupStepId,
} from '@shared/setup'
import { useAuth } from '../../../contexts/auth-context'
import { useVoiceCallSession } from '../../../hooks/use-voice-call-session'
import { updateMySetupState } from '../../../services/appsync-client'
import { useActiveSession } from '../../../stores/active-session-store'
import { usePendingRecordingPolicyGate } from '../../../stores/recording-policy-gate-store'
import { useIsAppLocked } from '../../../stores/app-lock-store'
import { loadSetupProfile, patchSetupProfile } from '../../../stores/setup-profile-store'
import { isTerminalCallPhase } from '../../../stores/voice-call-store'
import { getNativePlatform, isNativeApp } from '../../../utils/platform'
import { shouldShowTutorial, TUTORIAL_VERSION } from '../../onboarding-tour/tour-version'
import { useActiveSessionRecording } from '../../session-recording/hooks/use-active-session-recording'
import { probeThisDeviceApproval } from '../data/device-approval-probe'
import { withTimeout } from '../utils/with-timeout'
import {
  clearSetupRequest,
  requestSetup,
  resetSetupGate,
  setSetupOpen,
  setSetupOutstanding,
  setSetupPending,
  useSetupGate,
} from '../data/setup-gate'
import { pushDiagnostics } from '../../../stores/push-diagnostics-store'
import {
  clearLatestDeviceReport,
  deviceReportForSync,
  onSetupProbeRefresh,
  requestDeviceReportNow,
  setLatestDeviceReport,
} from '../data/setup-report-store'
import {
  clearAskedForResetRun,
  clearLegacyPromptKeys,
  isFirstRunOwed,
  isResetRun,
  markConsentRequestShown,
  markFirstRunComplete,
  markPermissionsResetHonoured,
  markStepRequestsShown,
  readLocalSetupState,
} from '../data/setup-state'
import { useSetupModel } from '../hooks/use-setup-model'
import {
  consentRequestReminder,
  decideAutoSetup,
  planReminders,
  shouldPromptOnResume,
  stepsToWalk,
  type SetupMode,
} from '../utils/setup-model'
import { SETUP_FLOW_ATTR, SetupFlow, type SetupFlowOutcome } from './setup-flow'
import { SetupReminderBanner } from './setup-reminder-banner'

/** Never on sign-in / public pages, nor while someone answers a session-recording consent request. */
export const SETUP_BLOCKED_PREFIXES = ['/sign-in', '/auth', '/privacy', '/terms', '/delete-account', '/recording-consent/']

/** If setup can't decide in time (profile or checks stuck), stop holding the walkthrough. */
const PENDING_GIVE_UP_MS = 20_000
/** Batch report sends after a burst of step changes. */
const REPORT_DEBOUNCE_MS = 1_500
/** A fresh profile read never holds setup (or the walkthrough) longer than this. */
const PROFILE_REFRESH_TIMEOUT_MS = 8_000

type OpenFlow = {
  key: number
  mode: SetupMode
  walk: SetupStepId[]
  startStep?: SetupStepId
  /** Straight to the first step, without the reminder card (a banner tap). */
  startOnFirstStep: boolean
  resetRun: boolean
  consentRequestPending: boolean
  /** Finishing this walk counts as finishing first-run setup on this device. */
  completesFirstRun: boolean
  /** This walk honours an admin "Reset device permissions" when it finishes. */
  permissionsRun: boolean
}

/** Any other modal on screen (setup's own root excluded): setup waits for it. */
function otherModalOpen(): boolean {
  if (typeof document === 'undefined') return false
  for (const node of document.querySelectorAll('[role="dialog"][aria-modal="true"]')) {
    if (!node.closest(`[${SETUP_FLOW_ATTR}]`)) return true
  }
  return false
}

function isIosSafariOffHomeScreen(): boolean {
  if (isNativeApp() || typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
  const standalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches)
  return ios && !standalone
}

let appBuildCache: string | null = null
async function appBuild(): Promise<string> {
  if (appBuildCache) return appBuildCache
  if (!isNativeApp()) return (appBuildCache = 'web')
  try {
    const info = await CapacitorApp.getInfo()
    appBuildCache = `${getNativePlatform()} ${info.build} (${info.version})`
  } catch {
    appBuildCache = getNativePlatform()
  }
  return appBuildCache
}

/**
 * Runs first-run setup: decides when it shows (cold start, back after 15+
 * minutes, an admin request, Menu), keeps it off screen while something more
 * important is happening, reports this device's setup to the server, and hands
 * over to the walkthrough when done.
 */
export function SetupHost() {
  const { user, isLoading } = useAuth()
  const userId = user?.userId ?? null
  const location = useLocation()
  const navigate = useNavigate()
  const model = useSetupModel()
  const gate = useSetupGate()
  const callSession = useVoiceCallSession()
  const softLocked = useIsAppLocked()
  const recording = useActiveSessionRecording()
  const activeSession = useActiveSession()
  const preDialGate = usePendingRecordingPolicyGate()

  const [flow, setFlow] = useState<OpenFlow | null>(null)
  /** This app open reminds with the banner; the steps on it stay live. */
  const [bannerArmed, setBannerArmed] = useState(false)
  const [bannerHidden, setBannerHidden] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [devicePending, setDevicePending] = useState(false)
  const [trigger, setTrigger] = useState<'launch' | 'resume' | null>(null)
  /** A `?setup=` link is waiting for the fresh profile: it wins over the automatic start. */
  const [deepLinkPending, setDeepLinkPending] = useState(false)
  const keyRef = useRef(0)
  const launchedFor = useRef<string | null>(null)
  const currentUser = useRef<string | null>(userId)
  currentUser.current = userId

  const refreshAccount = useRef(model.refreshAccount)
  refreshAccount.current = model.refreshAccount

  /**
   * Re-read the profile and account permissions (bounded), then act — unless
   * another account signed in meanwhile.
   */
  const afterFreshProfile = useCallback((forUser: string, then: () => void) => {
    const fresh = Promise.all([loadSetupProfile(forUser, { force: true }), refreshAccount.current()])
    void withTimeout(fresh.then(() => undefined), undefined, PROFILE_REFRESH_TIMEOUT_MS).finally(() => {
      if (currentUser.current === forUser) then()
    })
  }, [])

  const onBlockedRoute = SETUP_BLOCKED_PREFIXES.some((p) => location.pathname.startsWith(p))
  const callActive = !!callSession && !isTerminalCallPhase(callSession.phase) && callSession.phase !== 'elsewhere'
  const recordingActive =
    recording.ref != null && ['starting', 'recording', 'paused'].includes(recording.snapshot.state)
  const travelling = activeSession?.phase === 'travelling'
  /** Hides setup at once (and keeps its place) while true. */
  const hardGate =
    !userId ||
    isLoading ||
    onBlockedRoute ||
    callActive ||
    recordingActive ||
    travelling ||
    softLocked ||
    preDialGate != null
  /** Holds back an automatic start. */
  const startGate = hardGate || modalOpen || !model.ready

  const serverProfile = model.profile.status === 'loaded' ? model.profile.profile : null
  const consentRequestedAt = parseSetupRequests(serverProfile?.setupRequests).consentRequestedAt ?? null
  const reminders = planReminders({
    rows: model.rows,
    local: model.local,
    consentRequest: consentRequestReminder({
      consentRequestPending: model.consentRequestPending,
      consentRequestedAt,
      local: model.local,
    }),
    adminSteps: Object.keys(pendingStepRequests(model.local.stepRequestsShownFor, serverProfile)) as SetupStepId[],
  })

  // New account on this device: decide again; old prompts' leftovers go.
  useEffect(() => {
    resetSetupGate()
    setDeepLinkPending(false)
    setFlow(null)
    setBannerArmed(false)
    setBannerHidden(false)
    clearLatestDeviceReport()
    if (userId) clearLegacyPromptKeys()
    else launchedFor.current = null
  }, [userId])

  // Trigger: the app was opened (once per launch per account).
  useEffect(() => {
    if (!userId || isLoading || launchedFor.current === userId) return
    launchedFor.current = userId
    setTrigger('launch')
  }, [userId, isLoading])

  // Trigger: back after 15+ minutes away. Re-read admin requests and the device first.
  const hiddenAt = useRef<number | null>(null)
  const refreshProbes = model.refreshProbes

  // The device report samples the push token's backend status; a launch probe
  // usually runs BEFORE the token sync finishes, which froze "Push registered:
  // No" into the report until the next foreground. Re-probe when the backend
  // status settles so the report (and the admin view) is right within seconds.
  useEffect(() => {
    if (!userId) return
    let last = pushDiagnostics.getSnapshot().backendStatus
    return pushDiagnostics.subscribe(() => {
      const status = pushDiagnostics.getSnapshot().backendStatus
      if (status === last) return
      last = status
      if (status === 'success' || status === 'error') void refreshProbes()
    })
  }, [userId, refreshProbes])

  // A permission changed outside setup (Device permissions page, return from
  // OS Settings): re-probe now so the report follows at once.
  useEffect(() => onSetupProbeRefresh(() => void refreshProbes()), [refreshProbes])
  useEffect(() => {
    if (!userId) return
    const onHidden = () => {
      if (hiddenAt.current == null) hiddenAt.current = Date.now()
    }
    const onVisible = () => {
      const resumed = shouldPromptOnResume(hiddenAt.current, Date.now())
      hiddenAt.current = null
      void refreshProbes()
      // Decide only on the fresh profile: an admin Reset / Resend may have landed meanwhile.
      if (resumed) afterFreshProfile(userId, () => setTrigger('resume'))
    }
    const onVisibility = () => (document.visibilityState === 'visible' ? onVisible() : onHidden())
    document.addEventListener('visibilitychange', onVisibility)
    let removeNative: (() => void) | undefined
    let disposed = false
    if (isNativeApp()) {
      void CapacitorApp.addListener('appStateChange', ({ isActive }) => (isActive ? onVisible() : onHidden())).then(
        (handle) => {
          if (disposed) void handle.remove()
          else removeNative = () => void handle.remove()
        },
      )
    }
    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', onVisibility)
      removeNative?.()
    }
  }, [userId, refreshProbes, afterFreshProfile])

  // Trigger: `?setup=<step>` (push tap, Alerts row, link) — open there and tidy the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const value = params.get(SETUP_DEEP_LINK_PARAM)
    // Cold start from a push tap: keep the link until sign-in has settled.
    if (value == null || !userId) return
    params.delete(SETUP_DEEP_LINK_PARAM)
    const search = params.toString()
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true })
    const step = isSetupStepId(value) ? value : 'overview'
    // A push tap is about a request made moments ago: open on the fresh profile.
    setDeepLinkPending(true)
    afterFreshProfile(userId, () => {
      requestSetup({ mode: 'deep-link', step })
      setDeepLinkPending(false)
    })
  }, [location.search, location.pathname, navigate, userId, afterFreshProfile])

  // Other dialogs (sign-in MFA, update prompt, the walkthrough…) come first.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const sync = () => setModalOpen(otherModalOpen())
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['role', 'aria-modal'] })
    return () => observer.disconnect()
  }, [])

  // Don't hold the walkthrough forever if setup can never decide.
  useEffect(() => {
    // A call, recording or travel is a legitimate wait: the walkthrough waits with setup.
    if (!userId || !gate.pending || flow || hardGate) return
    const id = window.setTimeout(() => setSetupPending(false), PENDING_GIVE_UP_MS)
    return () => window.clearTimeout(id)
  }, [userId, gate.pending, flow, hardGate])

  const openFlow = useCallback(
    (
      mode: SetupMode,
      startAt?: SetupStepId | 'overview',
      opts: {
        only?: readonly SetupStepId[]
        startOnFirstStep?: boolean
        adminSteps?: readonly SetupStepId[]
        permissionsRun?: boolean
      } = {},
    ) => {
      if (!userId) return false
      const resetRun = isResetRun(model.local, serverProfile) && mode !== 'reminder'
      const local = resetRun ? clearAskedForResetRun(userId) : readLocalSetupState(userId)
      const only = opts.only
      const walk = stepsToWalk({
        mode,
        rows: model.rows,
        local,
        resetRun,
        consentRequestPending: model.consentRequestPending,
        adminSteps: opts.adminSteps,
        startAt,
      }).filter((id) => !only || only.includes(id))
      const startStep = startAt && startAt !== 'overview' && walk.includes(startAt) ? startAt : undefined
      if (mode === 'deep-link' && startAt && startAt !== 'overview' && !startStep) return false
      if (mode === 'reminder' && walk.length === 0) return false
      keyRef.current += 1
      setFlow({
        key: keyRef.current,
        mode,
        walk,
        startStep,
        startOnFirstStep: opts.startOnFirstStep === true,
        resetRun,
        consentRequestPending: model.consentRequestPending,
        completesFirstRun: mode === 'first-run' || mode === 'manual' || (mode === 'deep-link' && !startStep),
        permissionsRun: opts.permissionsRun === true,
      })
      setBannerArmed(false)
      setSetupOpen(true)
      setSetupPending(true)
      model.refreshLocal()
      if (!startStep) {
        void probeThisDeviceApproval().then((s) => setDevicePending(s === 'pending'))
      }
      return true
    },
    [userId, model, serverProfile],
  )

  // Automatic start on a trigger, once every gate is clear.
  useEffect(() => {
    if (!trigger || flow || startGate || gate.request || deepLinkPending) return
    setTrigger(null)
    const requests = parseSetupRequests(serverProfile?.setupRequests)
    const firstRunOwed = isFirstRunOwed(model.local, serverProfile)
    const consentRequest = consentRequestReminder({
      consentRequestPending: model.consentRequestPending,
      consentRequestedAt: requests.consentRequestedAt ?? null,
      local: model.local,
    })
    const pendingSteps = pendingStepRequests(model.local.stepRequestsShownFor, serverProfile)
    const adminSteps = Object.keys(pendingSteps) as SetupStepId[]
    const decision = decideAutoSetup({ rows: model.rows, local: model.local, firstRunOwed, consentRequest, adminSteps })
    if (decision === 'banner') {
      setBannerArmed(true)
      setBannerHidden(false)
    }
    if (decision === 'first-run' && openFlow('first-run')) return
    // An admin "Reset device permissions" this device hasn't walked: the
    // device-permission steps come back (like a reset, scoped to permissions).
    // Like a reset, it returns on every open until the walk finishes once —
    // "Later" doesn't downgrade it to the banner.
    if (!firstRunOwed && isPermissionsResetPending(model.local.honouredPermissionsResetAt, serverProfile)) {
      // A consent request still at its popup stage joins this walk instead of
      // being displaced by it (the admin asked for both).
      const only: readonly SetupStepId[] =
        consentRequest === 'popup' ? [...DEVICE_SETUP_STEPS, 'recording-consent'] : DEVICE_SETUP_STEPS
      if (
        openFlow('reminder', undefined, {
          only,
          adminSteps: DEVICE_SETUP_STEPS,
          permissionsRun: true,
        })
      ) {
        return
      }
      // Nothing to walk — every device permission is already fine. Honour silently.
      if (userId && requests.permissionsResetAt) {
        markPermissionsResetHonoured(userId, requests.permissionsResetAt)
        model.refreshLocal()
      }
    }
    if (decision === 'reminder') {
      // Only what hasn't been put off yet; the rest waits for the banner next time.
      const { popup } = planReminders({ rows: model.rows, local: model.local, consentRequest, adminSteps })
      if (openFlow('reminder', undefined, { only: popup, adminSteps })) return
    }
    setSetupPending(false)
  }, [trigger, flow, startGate, gate.request, deepLinkPending, model, serverProfile, openFlow, userId])

  // Explicit opens (Menu, push tap, deep link) skip the "other modal" wait.
  useEffect(() => {
    const request = gate.request
    if (!request || flow || hardGate || !model.ready) return
    clearSetupRequest()
    if (!openFlow(request.mode, request.step)) setSetupPending(false)
  }, [gate.request, flow, hardGate, model.ready, openFlow])

  // Device report: what this device has, and how far setup got here.
  const reportTimer = useRef<number | null>(null)
  const scheduleReport = useCallback(() => {
    if (reportTimer.current) window.clearTimeout(reportTimer.current)
    reportTimer.current = window.setTimeout(() => {
      reportTimer.current = null
      requestDeviceReportNow()
    }, REPORT_DEBOUNCE_MS)
  }, [])
  useEffect(() => {
    if (!userId || !model.ready || !model.facts || !model.probes) return
    const summary = summarizeSetup(model.requirements, model.facts, model.local.optionalAsked)
    const owed = isFirstRunOwed(model.local, serverProfile)
    const permissions = model.probes.permissions
    let cancelled = false
    void appBuild().then((build) => {
      if (cancelled) return
      setLatestDeviceReport(userId, {
        permissions: { v: 1, ...permissions },
        setup: {
          v: 1,
          flowVersion: model.local.completedAt ? model.local.flowVersion || SETUP_FLOW_VERSION : SETUP_FLOW_VERSION,
          completedAt: owed ? null : model.local.completedAt,
          outstanding: summary.outstanding,
          skipped: summary.skipped,
          appBuild: build,
        },
      })
      // A new or changed report goes out now instead of at the next foreground.
      if (deviceReportForSync(userId, Date.now())) scheduleReport()
    })
    setSetupOutstanding(summary.outstanding.length)
    return () => {
      cancelled = true
    }
  }, [userId, model.ready, model.facts, model.probes, model.requirements, model.local, serverProfile, scheduleReport])

  const refreshLocal = model.refreshLocal
  const onChanged = useCallback(() => {
    refreshLocal()
    scheduleReport()
  }, [refreshLocal, scheduleReport])
  useEffect(
    () => () => {
      if (reportTimer.current) window.clearTimeout(reportTimer.current)
    },
    [],
  )

  const onRefresh = useCallback(async () => {
    await model.refreshProbes()
  }, [model])

  const onClose = useCallback(
    (outcome: SetupFlowOutcome) => {
      const current = flow
      setFlow(null)
      setTrigger(null)
      setSetupOpen(false)
      setSetupPending(false)
      if (!userId || !current) return
      if (current.consentRequestPending && consentRequestedAt && current.walk.includes('recording-consent')) {
        markConsentRequestShown(userId, consentRequestedAt)
      }
      // Per-step admin requests this walk put in front of the person: shown once,
      // then the normal reminders (required steps) or nothing (optional) take over.
      const pendingSteps = pendingStepRequests(model.local.stepRequestsShownFor, serverProfile)
      const shown: Partial<Record<SetupStepId, string>> = {}
      for (const [step, at] of Object.entries(pendingSteps) as [SetupStepId, string][]) {
        if (current.walk.includes(step)) shown[step] = at
      }
      if (Object.keys(shown).length > 0) markStepRequestsShown(userId, shown)
      if (outcome === 'completed') {
        const requests = parseSetupRequests(serverProfile?.setupRequests)
        // A finished permissions walk (or a full first-run/reset walk, which
        // covers every device permission) honours the admin permissions reset.
        if ((current.permissionsRun || current.completesFirstRun) && requests.permissionsResetAt) {
          markPermissionsResetHonoured(userId, requests.permissionsResetAt)
        }
        if (current.completesFirstRun) {
          markFirstRunComplete(userId, {
            nowIso: new Date().toISOString(),
            honouredResetAt: requests.setupResetAt ?? null,
          })
          void updateMySetupState({ lastCompletedFlowVersion: SETUP_FLOW_VERSION })
            .then((res) => patchSetupProfile(userId, { setupState: res.setupState }))
            .catch(() => {
              /* informational only */
            })
        }
      }
      onChanged()
    },
    [flow, userId, serverProfile, consentRequestedAt, model.local.stepRequestsShownFor, onChanged],
  )

  const tourNext = shouldShowTutorial() && !hasSeenTourVersion(serverProfile?.setupState, TUTORIAL_VERSION)
  const consentStatus = (() => {
    const raw = (serverProfile?.recordingPolicyConsentStatus ?? '').toUpperCase()
    return raw === 'ACCEPTED' || raw === 'DECLINED' ? raw : null
  })()

  return (
    <>
      {flow && model.copy && (
        <SetupFlow
          key={flow.key}
          mode={flow.mode}
          walk={flow.walk}
          rows={model.rows}
          copy={model.copy}
          startStep={flow.startStep}
          startOnFirstStep={flow.startOnFirstStep}
          resetRun={flow.resetRun}
          consentRequestPending={flow.consentRequestPending}
          devicePending={devicePending}
          showUseAppTip={isIosSafariOffHomeScreen()}
          tourNext={tourNext}
          roleType={serverProfile?.roleType ?? user?.roleType ?? null}
          consentStatus={consentStatus}
          hidden={hardGate}
          onRefresh={onRefresh}
          onChanged={onChanged}
          onClose={onClose}
        />
      )}
      {!flow && bannerArmed && !bannerHidden && !hardGate && reminders.banner.length > 0 && (
        <SetupReminderBanner
          steps={reminders.banner}
          platform={model.copy?.platform ?? 'web'}
          onFix={() => {
            if (!openFlow('reminder', undefined, { only: reminders.banner, startOnFirstStep: true })) {
              setBannerArmed(false)
            }
          }}
          onHide={() => setBannerHidden(true)}
        />
      )}
    </>
  )
}
